import React from 'react';
import { Box, Container, Grid, Skeleton, Stack } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { withAlpha } from '../../components/ContestDetailsV2/utils';

const skeletonBlock = (t: (typeof theme)['dark'], height: number | string, width = '100%') => ({
    bgcolor: withAlpha(t.true_white, 0.08),
    borderRadius: 1,
    height,
    width,
});

const ViewContestShimmer = () => {
    const { themeStyle } = useThemeContext();
    const t = theme[themeStyle];

    return (
        <Container
            disableGutters
            maxWidth={false}
            sx={{
                boxSizing: 'border-box',
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                minHeight: '100vh',
                marginTop: 0,
                marginLeft: 0,
                px: { xs: 2, sm: 3, lg: 4 },
                overflowX: 'hidden',
                pb: 10,
            }}>
            {/* Hero — matches ContestDetailHero full-bleed strip */}
            <Box
                sx={{
                    mx: { xs: -2, sm: -3, lg: -4 },
                    width: { xs: 'calc(100% + 32px)', sm: 'calc(100% + 48px)', lg: 'calc(100% + 64px)' },
                }}>
                <Box
                    sx={{
                        position: 'relative',
                        height: { xs: 400, md: 440 },
                        width: '100%',
                        borderRadius: 0,
                        borderBottomLeftRadius: 3,
                        borderBottomRightRadius: 3,
                        overflow: 'hidden',
                        background: `linear-gradient(135deg, ${withAlpha(t.highlight, 0.15)} 0%, ${t.pure_black} 55%)`,
                    }}>
                    <Skeleton
                        variant="rectangular"
                        animation="wave"
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            bgcolor: withAlpha(t.true_white, 0.06),
                            transform: 'none',
                        }}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            p: { xs: 2, md: 3 },
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}>
                        <Skeleton
                            variant="circular"
                            animation="wave"
                            width={40}
                            height={40}
                            sx={{ bgcolor: withAlpha(t.true_white, 0.12) }}
                        />
                        <Skeleton
                            variant="rounded"
                            animation="wave"
                            width={160}
                            height={36}
                            sx={{ borderRadius: 2, bgcolor: withAlpha(t.true_white, 0.12) }}
                        />
                    </Box>
                    <Stack
                        spacing={1.5}
                        sx={{
                            position: 'absolute',
                            bottom: { xs: 24, md: 32 },
                            left: { xs: 16, md: 24 },
                            right: { xs: 16, md: 24 },
                            maxWidth: { md: '62%' },
                        }}>
                        <Skeleton
                            variant="rounded"
                            animation="wave"
                            height={28}
                            width="85%"
                            sx={{ ...skeletonBlock(t, 28, '85%'), maxWidth: 420 }}
                        />
                        <Skeleton
                            variant="rounded"
                            animation="wave"
                            height={22}
                            width="55%"
                            sx={{ ...skeletonBlock(t, 22, '55%'), maxWidth: 280 }}
                        />
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {[72, 88, 64].map(w => (
                                <Skeleton
                                    key={w}
                                    variant="rounded"
                                    animation="wave"
                                    width={w}
                                    height={28}
                                    sx={{ borderRadius: 4, bgcolor: withAlpha(t.true_white, 0.1) }}
                                />
                            ))}
                        </Box>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ pt: 1 }}>
                            <Skeleton
                                variant="rounded"
                                animation="wave"
                                height={44}
                                sx={{ flex: 1, maxWidth: 200, borderRadius: 6, bgcolor: withAlpha(t.highlight, 0.35) }}
                            />
                            <Skeleton
                                variant="rounded"
                                animation="wave"
                                height={44}
                                sx={{ flex: 1, maxWidth: 160, borderRadius: 6, bgcolor: withAlpha(t.true_white, 0.12) }}
                            />
                        </Stack>
                    </Stack>
                </Box>
            </Box>

            <Box sx={{ maxWidth: 1152, mx: 'auto', width: '100%', minWidth: 0, mt: 6 }}>
                <Grid container spacing={{ xs: 3, md: 4 }}>
                    {/* Left column — brief, timeline, carousels */}
                    <Grid item xs={12} md={7} sx={{ minWidth: 0 }}>
                        <Stack spacing={3}>
                            <Skeleton
                                variant="text"
                                animation="wave"
                                width={140}
                                sx={{ fontSize: 12, bgcolor: withAlpha(t.true_white, 0.08) }}
                            />
                            <Skeleton
                                variant="text"
                                animation="wave"
                                width="92%"
                                sx={{ fontSize: 28, bgcolor: withAlpha(t.true_white, 0.1) }}
                            />
                            <Stack spacing={1}>
                                <Skeleton variant="text" animation="wave" width="100%" sx={{ fontSize: 14 }} />
                                <Skeleton variant="text" animation="wave" width="100%" sx={{ fontSize: 14 }} />
                                <Skeleton variant="text" animation="wave" width="78%" sx={{ fontSize: 14 }} />
                            </Stack>
                            <Box
                                sx={{
                                    p: 2.5,
                                    borderRadius: 2,
                                    border: `1px solid ${withAlpha(t.true_white, 0.08)}`,
                                    bgcolor: withAlpha(t.true_white, 0.03),
                                }}>
                                <Skeleton variant="text" width={100} sx={{ fontSize: 12, mb: 2 }} />
                                {[1, 2, 3].map(i => (
                                    <Stack key={i} direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
                                        <Skeleton variant="circular" width={10} height={10} />
                                        <Box sx={{ flex: 1 }}>
                                            <Skeleton variant="text" width={`${85 - i * 12}%`} sx={{ fontSize: 13 }} />
                                        </Box>
                                    </Stack>
                                ))}
                            </Box>
                            <Skeleton variant="text" width={160} sx={{ fontSize: 20, pt: 1 }} />
                            <Stack direction="row" spacing={2} sx={{ overflow: 'hidden' }}>
                                {[1, 2, 3].map(i => (
                                    <Skeleton
                                        key={i}
                                        variant="rounded"
                                        animation="wave"
                                        sx={{
                                            flexShrink: 0,
                                            width: { xs: 'min(72vw, 260px)', sm: 220 },
                                            height: 320,
                                            borderRadius: 2,
                                            bgcolor: withAlpha(t.true_white, 0.07),
                                        }}
                                    />
                                ))}
                            </Stack>
                        </Stack>
                    </Grid>

                    {/* Right column — sidebar cards + actions */}
                    <Grid item xs={12} md={5} sx={{ minWidth: 0 }}>
                        <Stack spacing={2}>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Skeleton
                                        variant="rounded"
                                        animation="wave"
                                        height={180}
                                        sx={{
                                            borderRadius: 2,
                                            bgcolor: withAlpha(t.true_white, 0.06),
                                            border: `1px solid ${withAlpha(t.true_white, 0.1)}`,
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <Skeleton
                                        variant="rounded"
                                        animation="wave"
                                        height={180}
                                        sx={{
                                            borderRadius: 2,
                                            bgcolor: withAlpha(t.true_white, 0.06),
                                            border: `1px solid ${withAlpha(t.true_white, 0.1)}`,
                                        }}
                                    />
                                </Grid>
                            </Grid>
                            {[1, 2, 3].map(i => (
                                <Skeleton
                                    key={i}
                                    variant="rounded"
                                    animation="wave"
                                    height={56}
                                    sx={{
                                        borderRadius: 2,
                                        bgcolor: withAlpha(t.true_white, 0.05),
                                        border: `1px solid ${withAlpha(t.true_white, 0.08)}`,
                                    }}
                                />
                            ))}
                            <Skeleton
                                variant="rounded"
                                animation="wave"
                                height={120}
                                sx={{
                                    borderRadius: 2,
                                    mt: 1,
                                    bgcolor: withAlpha(t.true_white, 0.05),
                                }}
                            />
                        </Stack>
                    </Grid>
                </Grid>

                <Stack spacing={2} sx={{ mt: 6 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Skeleton variant="text" width={180} sx={{ fontSize: 22 }} />
                        <Skeleton variant="text" width={72} sx={{ fontSize: 14 }} />
                    </Stack>
                    <Stack direction="row" spacing={2} sx={{ overflow: 'hidden' }}>
                        {[1, 2, 3, 4].map(i => (
                            <Skeleton
                                key={i}
                                variant="rounded"
                                animation="wave"
                                sx={{
                                    flexShrink: 0,
                                    width: 200,
                                    height: 200,
                                    borderRadius: 2,
                                    bgcolor: withAlpha(t.true_white, 0.07),
                                }}
                            />
                        ))}
                    </Stack>
                </Stack>
            </Box>
        </Container>
    );
};

export { ViewContestShimmer };
